<template>
  <div>
    <h1 class="heading-1 mb-24">{{ i18n(translations.title) }}</h1>
    <p class="body-1 mb-24">{{ i18n(translations.description) }}</p>
    <RevForm
      :id="FORM_ID"
      :initial-values="initialFormValues"
      :validate="validate()"
      @submit="handleSubmit"
    >
      <template #default="{ values, errors, hasError }">
        <div class="space-y-36">
          <div class="space-y-12">
            <template v-if="!isJapan">
              <RevInputText
                id="legal_name"
                v-model="values.legalName"
                :error="errors.legalName"
                :label="i18n(translations.legalName)"
              />
              <RevInputText
                id="trade_name"
                v-model="values.tradeName"
                :error="errors.tradeName"
                :label="i18n(translations.tradeName)"
              />
            </template>
            <template v-if="isJapan">
              <RevInputText
                id="legal_name"
                v-model="values.legalName"
                :error="errors.legalName"
                :label="i18n(translations.legalName)"
              />
              <RevInputText
                id="kanaLegalName"
                v-model="values.kanaLegalName"
                class="mb-12"
                :error="errors.kanaLegalName"
                :label="i18n(translations.kanaLegalName)"
                type="text"
              />
              <RevInputText
                id="latinLegalName"
                v-model="values.latinLegalName"
                class="mb-12"
                :error="errors.latinLegalName"
                :label="i18n(translations.latinLegalName)"
                type="text"
              />
            </template>
            <RevInputText
              id="email"
              v-model="values.email"
              :error="errors.email"
              :label="i18n(translations.email)"
              type="email"
            />
          </div>
          <div class="space-y-12">
            <RevInputSelect
              id="country_of_registration"
              v-model="values.countryOfRegistration"
              :error="errors.countryOfRegistration"
              :label="i18n(translations.countryOfRegistration)"
              :options="countries"
            />
            <RevInputSelect
              id="registration_type"
              v-model="values.registrationType"
              :error="errors.registrationType"
              :label="i18n(translations.registrationType)"
              :options="registrationTypes"
            />
            <RevInputText
              id="employer_identification_number"
              v-model="values.registrationNumber"
              :error="errors.registrationNumber"
              :label="i18n(translations.registrationNumber)"
            />
            <RevInputDate
              id="incorporation_date"
              v-model="values.incorporationDate"
              :error="errors.incorporationDate"
              :label="i18n(translations.incorporationDate)"
            />
          </div>

          <template v-if="isJapan || isAustralia">
            <fieldset class="space-y-12">
              <legend class="body-1-bold mb-12">
                {{ i18n(translations.secondhandDealerNumberTitle) }}
              </legend>
              <RevInputText
                id="secondhand_dealer_number"
                v-model="values.secondhandDealerNumber"
                :error="errors.secondhandDealerNumber"
                :label="i18n(translations.secondhandDealerNumber)"
              />
              <RevInputSelect
                id="secondhand_dealer_number_issuing_prefecture"
                v-model="values.secondhandDealerNumberIssuingPrefecture"
                class="mb-12"
                data-cs-mask
                :error="errors.secondhandDealerNumberIssuingPrefecture"
                :label="i18n(translations.organizationType)"
                name="secondhandDealerNumberIssuingPrefecture"
                :options="secondhandDealerNumberIssuingPrefectures"
              />
            </fieldset>
            <fieldset class="space-y-12">
              <legend class="body-1-bold mb-12">
                {{ i18n(translations.proofOfSecondHandDealerTitle) }}
              </legend>
              <span class="text-static-default-low body-2">
                {{ i18n(translations.proofOfSecondHandDealerDescription) }}
              </span>
              <RevInputFileUpload
                id="secondhand_dealer_licence"
                v-model="values.proofOfSecondHandDealerLicence"
                :accept="ACCEPTED_FILE_TYPES"
                :description="
                  errors.proofOfSecondHandDealerLicence ??
                  i18n(translations.fileUploadDescription)
                "
                :errors="errors.proofOfSecondHandDealerLicence as any"
                :label-empty="i18n(translations.selectFile)"
              />
            </fieldset>
          </template>
          <fieldset class="space-y-12">
            <legend class="body-1-bold mb-12">
              {{ i18n(translations.headquartersAddress) }}
            </legend>

            <AddressFields
              v-model:city="values.headquartersCity"
              v-model:country="values.headquartersCountry"
              v-model:postal-code="values.headquartersPostalCode"
              v-model:street="values.headquartersStreet"
              v-model:street2="values.headquartersStreet2"
              class="max-w-[327px] space-y-12 space-y-reverse"
              :errors="{
                street: errors.headquartersStreet,
                city: errors.headquartersCity,
                postalCode: errors.headquartersPostalCode,
                country: errors.headquartersCountry,
                street2: errors.headquartersStreet2,
              }"
              :feature-code="FeatureCode.WEB_KYB_BUSINESS_DETAILS"
              :fields="{ firstName: false, lastName: false }"
              :optional-fields="['street2']"
            />
          </fieldset>

          <div class="space-y-12">
            <div
              v-for="(branch, index) in branchesAddresses"
              :key="index"
              class="space-y-12"
            >
              <div class="body-1-bold">
                {{ i18n(translations.branchAddress) }} {{ index + 1 }}
              </div>
              <AddressFields
                v-model:city="branch.city"
                v-model:country="branch.country"
                v-model:postal-code="branch.postalCode"
                v-model:street="branch.street"
                v-model:street2="branch.street2"
                class="max-w-[327px] space-y-12 space-y-reverse"
                :errors="{}"
                :feature-code="FeatureCode.WEB_KYB_BUSINESS_DETAILS"
                :fields="{ firstName: false, lastName: false }"
                :optional-fields="['street2']"
              />
              <div class="flex justify-end">
                <RevButton
                  size="small"
                  variant="secondaryDestructive"
                  @click="removeBranch(index)"
                >
                  {{ i18n(translations.removeBranch) }}
                </RevButton>
              </div>
            </div>
            <RevButton size="small" variant="secondary" @click="addBranch">
              {{ i18n(translations.addBranch) }}
            </RevButton>
          </div>

          <fieldset class="space-y-12">
            <legend class="body-1-bold mb-12">
              {{ i18n(translations.articleOfIncorporation) }}
            </legend>
            <span class="text-static-default-low body-2">
              {{ i18n(translations.articleOfIncorporationDescription) }}
            </span>
            <RevInputFileUpload
              id="article-of-incorporation-file-input"
              v-model="values.articleOfIncorporation"
              :accept="['.png', '.jpg', '.jpeg', '.pdf']"
              :description="
                errors.articleOfIncorporation ??
                i18n(translations.fileUploadDescription)
              "
              :errors="errors.articleOfIncorporation as any"
              :label-empty="i18n(translations.selectFile)"
              :max-files="1"
            />
          </fieldset>
          <fieldset v-if="isUS" class="space-y-12">
            <legend class="body-1-bold mb-12">
              {{ i18n(translations.w9FormTitle) }}
            </legend>

            <RevInputFileUpload
              id="w9-file-input"
              v-model="values.w9"
              :accept="['.png', '.jpg', '.jpeg', '.pdf']"
              :description="
                errors.w9 ?? i18n(translations.fileUploadDescription)
              "
              :errors="errors.w9 as any"
              :label-empty="i18n(translations.selectFile)"
            />
          </fieldset>
          <fieldset class="space-y-12">
            <legend class="body-1-bold mb-12">
              {{ i18n(translations.registeredBusinessAddress) }}
            </legend>
            <AddressFields
              v-model:city="values.registeredBusinessCity"
              v-model:country="values.registeredBusinessCountry"
              v-model:postal-code="values.registeredBusinessPostalCode"
              v-model:street="values.registeredBusinessStreet"
              v-model:street2="values.registeredBusinessStreet2"
              class="max-w-[327px] space-y-12 space-y-reverse"
              :errors="{
                street: errors.registeredBusinessStreet,
                city: errors.registeredBusinessCity,
                postalCode: errors.registeredBusinessPostalCode,
                country: errors.registeredBusinessCountry,
                street2: errors.registeredBusinessStreet2,
              }"
              :feature-code="FeatureCode.WEB_KYB_BUSINESS_DETAILS"
              :fields="{ firstName: false, lastName: false }"
              :optional-fields="['street2']"
            />
          </fieldset>
          <fieldset class="space-y-12">
            <legend class="body-1-bold mb-12">
              {{ i18n(translations.investedCapital) }}
            </legend>
            <RevInputSelect
              id="invested_capital_currency"
              v-model="values.investedCapitalCurrency"
              :error="errors.investedCapitalCurrency"
              :label="i18n(translations.investedCapitalCurrency)"
              :options="currencies"
            />
            <RevInputNumber
              id="invested_capital_amount"
              v-model="values.investedCapitalAmount"
              :error="errors.investedCapitalAmount"
              :label="i18n(translations.investedCapitalAmount)"
            />
          </fieldset>
        </div>
        <div
          class="mx-auto mt-48 flex w-[328px] items-center justify-center gap-16"
        >
          <RevButton
            class="w-full"
            :disabled="hasError"
            :form="FORM_ID"
            :loading="isLoading"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.saveButton) }}
          </RevButton>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { Country } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevButton } from '@ds/components/Button'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  maxLength,
  min,
  required,
} from '@ds/components/Form'
import { RevInputDate } from '@ds/components/InputDate'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevInputNumber } from '@ds/components/InputNumber'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import type { KybData } from '~/scopes/kyb/pages/Index.types'

import {
  COUNTRY_CODES,
  CURRENCIES,
  VALIDATION_REGEX,
} from './BusinessDetails.constant'
import translations from './BusinessDetails.translations'
import type { BusinessFormValues } from './BusinessDetails.types'
import { useBranchManagement } from './composables/useBranchManagement'

const i18n = useI18n()
const FORM_ID = 'kyb_business_details_form'
const ACCEPTED_FILE_TYPES = ['.png', '.jpg', '.jpeg', '.pdf']
const { marketplace, market: currentMarket } = useMarketplace()

const props = defineProps<{
  kybData: KybData
}>()

const businessDetails = computed(() => props.kybData?.business)

const sellerMarketplaceCode = computed(() => marketplace.marketplaceCode)

const currencies = computed(() => CURRENCIES[sellerMarketplaceCode.value])

// Market detection
const isJapan = computed(
  () => currentMarket.countryCode === MarketCountryCode.JP,
)
const isAustralia = computed(
  () => currentMarket.countryCode === MarketCountryCode.AU,
)
const isUS = computed(() => currentMarket.countryCode === MarketCountryCode.US)

const countries = computed(() =>
  COUNTRY_CODES.map((countryCode) => ({
    label: i18n.country(countryCode),
    value: countryCode,
  })),
)

// TODO: mock data, to be replaced by metadata payload
const secondhandDealerNumberIssuingPrefectures = computed(() => [
  { label: 'Tokyo', value: 'TOKYO' },
  { label: 'Osaka', value: 'OSAKA' },
  { label: 'Kyoto', value: 'KYOTO' },
  { label: 'Hiroshima', value: 'HIROSHIMA' },
  { label: 'Fukuoka', value: 'FUKUOKA' },
])

// TODO: dummy mock values, to be defined with Product and backend: GM-8685
const registrationTypes = computed(() => [
  { label: 'Corporation', value: 'CORPORATION' },
  { label: 'LLC', value: 'LLC' },
  { label: 'Partnership', value: 'PARTNERSHIP' },
  { label: 'Sole Proprietorship', value: 'SOLE_PROPRIETORSHIP' },
])

const isLoading = ref(false)

// Branch management
const {
  branches: branchesAddresses,
  addBranch,
  removeBranch,
} = useBranchManagement()

const initialFormValues = computed((): BusinessFormValues => {
  const business = businessDetails.value
  const { headquartersAddress, registeredBusinessAddress, investedCapital } =
    business

  return {
    legalName: business.legalName ?? '',
    tradeName: business.tradeName ?? '',
    kanaLegalName: business.legalName ?? '',
    latinLegalName: business.legalName ?? '',
    email: business?.email ?? '',
    countryOfRegistration: business.countryOfRegistration ?? '',
    registrationType: business.registrationType ?? '',
    registrationNumber: business.registrationNumber ?? '',
    secondhandDealerNumber: business.secondhandDealerNumber ?? '',
    secondhandDealerNumberIssuingPrefecture:
      business.secondhandDealerNumberIssuingPrefecture ?? '',
    proofOfSecondHandDealerLicence:
      business.proofOfSecondHandDealerLicence ?? [],
    incorporationDate: business.incorporationDate ?? '',
    headquartersStreet: headquartersAddress.addressLine1 ?? '',
    headquartersStreet2: headquartersAddress.addressLine2 ?? '',
    headquartersCity: headquartersAddress.city ?? '',
    headquartersPostalCode: headquartersAddress.postalCode ?? '',
    headquartersCountry: (headquartersAddress.country as Country | '') ?? '',
    registeredBusinessStreet: registeredBusinessAddress.addressLine1 ?? '',
    registeredBusinessStreet2: registeredBusinessAddress.addressLine2 ?? '',
    registeredBusinessCity: registeredBusinessAddress.city ?? '',
    registeredBusinessPostalCode: registeredBusinessAddress.postalCode ?? '',
    registeredBusinessCountry:
      (registeredBusinessAddress.country as Country | '') ?? '',
    branchesAddresses: branchesAddresses.value,
    w9: Array.isArray(business.w9) ? business.w9 : [],
    articleOfIncorporation: business.articleOfIncorporation ?? [],
    investedCapitalCurrency: investedCapital.currency ?? '',
    investedCapitalAmount: investedCapital.amount ?? 0,
  }
})

function validate() {
  return makeValidate<BusinessFormValues>({
    legalName: [
      required(i18n(translations.legalNameRequired)),
      maxLength(100, i18n(translations.legalNameMaxLength)),
      matchingRegExp(
        VALIDATION_REGEX.COMPANY_NAME,
        i18n(translations.legalNameInvalidCharacters),
      ),
    ],
    tradeName: [
      required(i18n(translations.tradeNameRequired)),
      maxLength(80, i18n(translations.tradeNameMaxLength)),
      matchingRegExp(
        VALIDATION_REGEX.COMPANY_NAME,
        i18n(translations.tradeNameInvalidCharacters),
      ),
    ],
    countryOfRegistration: [
      required(i18n(translations.countryOfRegistrationRequired)),
    ],
    registrationType: [required(i18n(translations.registrationTypeRequired))],
    registrationNumber: [
      required(i18n(translations.registrationNumberRequired)),
      maxLength(50, i18n(translations.registrationNumberMaxLength)),
      matchingRegExp(
        VALIDATION_REGEX.REGISTRATION_NUMBER,
        i18n(translations.registrationNumberInvalidCharacters),
      ),
    ],
    incorporationDate: [required(i18n(translations.incorporationDateRequired))],

    headquartersStreet: [
      required(i18n(translations.headquartersAddressStreetRequired)),
    ],
    headquartersCity: [
      required(i18n(translations.headquartersAddressCityRequired)),
    ],
    headquartersPostalCode: [
      required(i18n(translations.headquartersAddressPostalCodeRequired)),
    ],
    headquartersCountry: [
      required(i18n(translations.headquartersAddressCountryRequired)),
    ],

    w9: [required(i18n(translations.required))],
    articleOfIncorporation: [required(i18n(translations.required))],

    investedCapitalCurrency: [required(i18n(translations.required))],
    investedCapitalAmount: [
      required(i18n(translations.required)),
      min(1, i18n(translations.investedCapitalMinValue)),
    ],
  })
}

// TODO: Implement form submission logic
async function handleSubmit(formValues: BusinessFormValues) {
  try {
    isLoading.value = true
    // eslint-disable-next-line no-console
    console.log('Form submitted:', formValues)
  } catch (error) {
    // console.error('Form submission error:', error)
  } finally {
    isLoading.value = false
  }
}

// Branch management functions are now provided by the useBranchManagement composable
</script>
